import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { StudentGroupMaintenanceComponent } from '../../../maintenance/student-group-maintenance/student-group-maintenance.component';
import { DynamicConfirmModalComponent } from '../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { BookingAccessService } from '../../../_services/booking-access/booking-access.service';
import { ClientLinkWholeClient, NamedBookingGrant } from '../../../_models/maintenance/booking-access.model';
import { UserClient } from '../../../_models/admin/user-client.model';

/** A role tick changed on one of the person's clients; Client Users saves it. */
export interface ClientRoleChange {
  client: UserClient;
  checked: boolean;
  role: 'ADMIN' | 'CUSTODIAN' | 'REPORT';
}

/** One row of the person's client table: their roles on the client and, while booking access shows, its Can see choice. */
export interface ClientAccessRow {
  clientId: number;
  clientName: string | null;
  /** The person's roles on the client; null for a link the role list does not carry. */
  client: UserClient | null;
  /** The client link's Can see choice (WholeClient) and its last change; null until loaded. */
  link: ClientLinkWholeClient | null;
}

/**
 * The Can see options' values: Only bookings they are named on (WholeClient false), Every booking at this client (true).
 * The template writes them as static option values, so keep the two in step.
 */
export const CanSeeNamedOnly = 'named';
export const CanSeeEveryBooking = 'every';

/**
 * A client user's clients on Client Users, as one table: a row per client with the Admin, Custodian and Reports
 * ticks and, while booking access shows, the Can see choice (Only bookings they are named on, or Every booking at
 * this client: the link's WholeClient flag) with who changed it last. Choosing Every booking asks first, because it
 * widens what the person sees. Under the table, the bookings the person is named on, read-only; naming and removing
 * people happens on each booking's setup. Client Users decides whether booking access shows (the
 * clientusers.booking-grants switch and the BookingSetupView role feature) and saves the role ticks; the API checks
 * both again.
 */
@Component({
  selector: 'client-user-access',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './client-user-access.component.html',
  styleUrls: ['./client-user-access.component.css'],
})
export class ClientUserAccessComponent implements OnChanges {
  @Input() userId: number;
  /** The person's name, for the Can see choice's accessible name and its confirm. */
  @Input() userName = '';
  @Input() clients: UserClient[] = [];
  /** Whether the Can see column and Named on bookings show. Nothing is read until it is true. */
  @Input() accessVisible = false;
  @Output() roleChange = new EventEmitter<ClientRoleChange>();

  public links: ClientLinkWholeClient[] = [];
  public namedOn: NamedBookingGrant[] = [];
  public message = '';
  private loaded = false;

  constructor(
    private bookingAccess: BookingAccessService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(): void {
    if (this.accessVisible && !this.loaded) {
      this.loaded = true;
      this.load();
    }
  }

  /** A row per client the person has roles on, then any link the role list does not carry. */
  public get rows(): ClientAccessRow[] {
    const clients = this.clients ?? [];
    const rows: ClientAccessRow[] = clients.map(client => ({
      clientId: client.clientId,
      clientName: client.clientName,
      client,
      link: this.links.find(l => l.clientId === client.clientId) ?? null,
    }));
    this.links
      .filter(link => !clients.some(c => c.clientId === link.clientId))
      .forEach(link => rows.push({ clientId: link.clientId, clientName: link.clientName, client: null, link }));
    return rows;
  }

  public get liveNamedOn(): NamedBookingGrant[] {
    return this.namedOn.filter(n => n.isLive);
  }

  public load(): void {
    this.bookingAccess.getLinks(this.userId).subscribe({
      next: links => this.links = links,
      error: () => this.message = 'Could not load this person\'s client links. Please try again.',
    });
    this.bookingAccess.getNamedOn(this.userId).subscribe({
      next: named => this.namedOn = named,
      error: () => this.message = 'Could not load the bookings this person is named on. Please try again.',
    });
  }

  public changeRole(client: UserClient, event: Event, role: ClientRoleChange['role']): void {
    this.roleChange.emit({ client, checked: (event.target as HTMLInputElement).checked, role });
  }

  /** Opens the booking's setup, where the people named on it are added and removed. */
  public openBookingSetup(grant: NamedBookingGrant): void {
    this.dialog.open(StudentGroupMaintenanceComponent, {
      data: { placementId: grant.placementId, externalView: false },
      panelClass: 'group-maintenance-dialog'
    }).afterClosed().subscribe(() => this.load());
  }

  /** The person's name as the screen says it, or "This person" when the list has none. */
  public get personName(): string {
    return (this.userName ?? '').trim() || 'This person';
  }

  public optionFor(link: ClientLinkWholeClient): string {
    return link.wholeClient ? CanSeeEveryBooking : CanSeeNamedOnly;
  }

  /**
   * The Can see choice changed. Every booking at this client widens what the person sees, so it asks first and Cancel
   * puts the choice back; Only bookings they are named on saves straight away.
   */
  public chooseWholeClient(link: ClientLinkWholeClient, clientName: string | null, select: HTMLSelectElement): void {
    if (select.value !== CanSeeEveryBooking) {
      this.setWholeClient(link, false, select);
      return;
    }
    const client = clientName || 'this client';
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '480px',
      data: {
        title: 'Every booking at ' + client,
        confirmMessage: `${this.personName} will see every booking at ${client}, including ones they are not named on. Continue?`,
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
      },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.setWholeClient(link, true, select);
      } else {
        select.value = this.optionFor(link);
      }
    });
  }

  /** Saves the link's Can see choice; on an error the select goes back to what was saved. */
  public setWholeClient(link: ClientLinkWholeClient, wholeClient: boolean, select?: HTMLSelectElement): void {
    this.message = '';
    this.bookingAccess.setWholeClient(this.userId, link.clientId, wholeClient).subscribe({
      next: updated => Object.assign(link, updated),
      error: err => {
        if (select) {
          select.value = this.optionFor(link);
        }
        this.message = err?.message || 'Could not save which bookings this person can see. Please try again.';
        this.load();
      },
    });
  }
}
